import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';
import { nativeGoogleLogin, nativeAppleLogin } from './nativeAuth';

/**
 * The deep link callback URL for native OAuth flows (PKCE).
 * Must be registered in Supabase Auth → Redirect URLs.
 */
const NATIVE_REDIRECT = 'promptnote://auth-callback';

/**
 * Open OAuth URL in system browser for native mobile.
 * The browser will redirect to NATIVE_REDIRECT on completion,
 * which deeplink.js intercepts to exchange the code for a session.
 */
async function openNativeBrowserOAuth(provider, options = {}) {
  const { Browser } = await import('@capacitor/browser');
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: NATIVE_REDIRECT,
      skipBrowserRedirect: true,
      ...options,
    },
  });
  if (error) throw error;
  if (data?.url) {
    await Browser.open({ url: data.url });
  }
  return data;
}

/**
 * Sign in with Google OAuth.
 * Native: uses native SDK (no browser hop) on iOS and Android.
 * Web: standard OAuth redirect.
 */
export const signInWithGoogle = async () => {
  if (Capacitor.isNativePlatform()) {
    return nativeGoogleLogin();
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
      data: { app_id: 'promptnote' },
    },
  });
  if (error) throw error;
  return data;
};

/**
 * Sign in with Apple OAuth.
 * iOS: uses native SDK (no browser hop).
 * Android: browser OAuth with deep link callback.
 * Web: standard OAuth redirect.
 */
export const signInWithApple = async () => {
  if (Capacitor.getPlatform() === 'ios') {
    return nativeAppleLogin();
  }

  if (Capacitor.isNativePlatform()) {
    // Android: browser flow with deep link callback
    return openNativeBrowserOAuth('apple', { data: { app_id: 'promptnote' } });
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: window.location.origin,
      data: { app_id: 'promptnote' },
    },
  });
  if (error) throw error;
  return data;
};

/**
 * Sign in with GitHub OAuth.
 * Native: browser OAuth with deep link callback.
 * Web: standard OAuth redirect.
 */
export const signInWithGitHub = async () => {
  if (Capacitor.isNativePlatform()) {
    return openNativeBrowserOAuth('github', { data: { app_id: 'promptnote' } });
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'github',
    options: {
      redirectTo: window.location.origin,
      data: { app_id: 'promptnote' },
    },
  });
  if (error) throw error;
  return data;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: window.location.origin,
      data: { app_id: 'promptnote' },
    },
  });

  if (error) throw error;
  return data;
};

/**
 * Reset password - send reset email
 */
export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) throw error;
  return data;
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

/**
 * Get the current session
 */
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

/**
 * Get the current user
 */
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};
